import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Heart, Minus, Plus, Ruler, Star, Truck, ShieldCheck, RefreshCcw } from "lucide-react";
import { useAuth } from "@clerk/react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { img, inr, offPct, useGet, useAuthedHttp, errMsg } from "@/lib/api";
import { useShop, useSeo } from "@/lib/store";
import { ProductGrid } from "@/components/ProductCard";

const TOP_GUIDE = [["S", "40", "27", "19"], ["M", "42", "28", "20"], ["L", "44", "29", "21"], ["XL", "46", "30", "22"], ["XXL", "48", "31", "23"]];
const BOT_GUIDE = [["28", "28", "40", "30"], ["30", "30", "41", "31"], ["32", "32", "42", "32"], ["34", "34", "43", "32"], ["36", "36", "44", "33"]];

function SizeGuide({ bottom }) {
  const rows = bottom ? BOT_GUIDE : TOP_GUIDE;
  const head = bottom ? ["Size", "Waist", "Length", "Inseam"] : ["Size", "Chest", "Length", "Shoulder"];
  return (
    <Dialog>
      <DialogTrigger asChild><button className="text-xs underline flex items-center gap-1 text-grey hover:text-neon" data-testid="size-guide-button"><Ruler className="w-3.5 h-3.5" />Size guide</button></DialogTrigger>
      <DialogContent className="bg-[#171717] border-white/10" data-testid="size-guide-dialog">
        <DialogTitle className="font-display text-3xl">SIZE GUIDE (INCHES)</DialogTitle>
        <table className="w-full text-sm font-mono mt-2"><thead><tr className="text-neon">{head.map((h) => <th key={h} className="text-left py-2">{h}</th>)}</tr></thead>
          <tbody>{rows.map((r) => <tr key={r[0]} className="border-t border-white/10">{r.map((c, i) => <td key={i} className="py-2">{c}</td>)}</tr>)}</tbody></table>
        <p className="text-xs text-grey">{bottom ? "Relaxed/baggy fits sit lower on the waist." : "Oversized fit — size down for a regular look."}</p>
      </DialogContent>
    </Dialog>
  );
}

function Reviews({ product, reviews }) {
  const { isSignedIn } = useAuth();
  const call = useAuthedHttp();
  const qc = useQueryClient();
  const [f, setF] = useState({ name: "", rating: 5, text: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await call("post", `/products/${product.slug}/reviews`, f); toast.success("Review posted"); setF({ name: "", rating: 5, text: "" }); qc.invalidateQueries({ queryKey: [`/products/${product.slug}`] }); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <section className="mt-24 grid lg:grid-cols-[1fr_1.4fr] gap-12" data-testid="reviews-section">
      <div>
        <p className="eyebrow">Reviews</p>
        <p className="font-display text-7xl mt-3">{product.rating || "—"}<span className="text-2xl text-grey"> / 5</span></p>
        <p className="text-grey text-sm">{product.rating_count} ratings</p>
        {isSignedIn ? (
          <form onSubmit={submit} className="mt-8 space-y-3" data-testid="review-form">
            <input required className="field" placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="review-name-input" />
            <div className="flex gap-1">{[1, 2, 3, 4, 5].map((n) => <button type="button" key={n} onClick={() => setF({ ...f, rating: n })} data-testid={`review-star-${n}`}><Star className={`w-6 h-6 ${n <= f.rating ? "fill-current text-neon" : "text-grey"}`} /></button>)}</div>
            <textarea required rows={3} className="field" placeholder="How's the fit?" value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} data-testid="review-text-input" />
            <button className="btn-neon" data-testid="review-submit-button">Post review</button>
          </form>
        ) : <Link to="/sign-in" className="btn-ghost mt-8" data-testid="review-sign-in">Sign in to review</Link>}
      </div>
      <div className="space-y-4">
        {reviews.length === 0 && <p className="text-grey">No written reviews yet — be the first.</p>}
        {reviews.map((r) => (
          <div key={r.id} className="bg-[#171717] p-6" data-testid="review-item">
            <div className="flex gap-0.5">{[...Array(r.rating)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current text-neon" />)}</div>
            <p className="mt-3">{r.text}</p>
            <p className="font-mono text-xs text-grey mt-3">{r.name.toUpperCase()} · {new Date(r.created_at).toLocaleDateString()}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Product() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { add, toggleWish, inWish } = useShop();
  const { data, isLoading, error } = useGet(`/products/${slug}`);
  const p = data?.product;
  const [active, setActive] = useState(0);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [qty, setQty] = useState(1);
  useSeo(p?.title, p?.description);
  useEffect(() => { setActive(0); setSize(""); setQty(1); setColor(p?.colors?.[0] || ""); }, [p]);

  if (isLoading) return <div className="max-w-[1500px] mx-auto px-8 py-20"><div className="aspect-[4/3] bg-[#171717] animate-pulse" /></div>;
  if (error || !p) return <div className="py-32 text-center font-display text-4xl" data-testid="product-not-found">PRODUCT NOT FOUND</div>;
  const bottom = p.sizes.includes("32");
  const off = offPct(p);
  const go = (buy) => {
    if (!size) return toast.error("Select a size first");
    add(p, size, color, qty);
    if (buy) nav("/checkout");
  };
  const labels = ["Main", "Alt view", "Artwork"];

  return (
    <div className="max-w-[1500px] mx-auto px-4 sm:px-8 py-10" data-testid="product-page">
      <nav className="font-mono text-[11px] text-grey mb-6 tracking-widest"><Link to="/shop" className="hover:text-neon">SHOP</Link> / <Link to={`/shop/${p.category}`} className="hover:text-neon uppercase">{p.category.replace(/-/g, " ")}</Link></nav>
      <div className="grid lg:grid-cols-[1.25fr_1fr] gap-10 lg:gap-16">
        <div className="grid grid-cols-[72px_1fr] sm:grid-cols-[90px_1fr] gap-3">
          <div className="flex flex-col gap-3">
            {p.images.map((u, i) => (
              <button key={i} onClick={() => setActive(i)} data-testid={`product-thumb-${i}`} className={`relative aspect-[3/4] overflow-hidden border-2 ${active === i ? "border-neon" : "border-transparent"}`}>
                <img src={img(u)} alt="" className="w-full h-full object-cover" loading="lazy" />
              </button>
            ))}
          </div>
          <div className="relative aspect-[3/4] bg-[#171717] overflow-hidden">
            <img key={active} src={img(p.images[active])} alt={p.title} className="w-full h-full object-cover reveal" data-testid="product-main-image" />
            <span className="absolute bottom-4 left-4 bg-black/60 backdrop-blur px-3 py-1 font-mono text-[10px] tracking-widest">{(labels[active] || `View ${active + 1}`).toUpperCase()}</span>
          </div>
        </div>

        <div className="lg:sticky lg:top-24 self-start">
          {p.tags?.includes("limited") && <span className="bg-red-600 text-white font-mono text-[10px] px-2 py-1">LIMITED EDITION</span>}
          <h1 className="font-display text-4xl sm:text-5xl leading-[0.95] mt-3" data-testid="product-title">{p.title}</h1>
          <div className="flex items-center gap-2 mt-3 text-sm text-grey"><Star className="w-4 h-4 fill-current text-neon" />{Number(p.rating).toFixed(1)} · {p.rating_count} reviews</div>
          <div className="flex items-baseline gap-3 mt-6 font-mono">
            <span className="text-3xl font-semibold" data-testid="product-detail-price">{inr(p.effective_price || p.price)}</span>
            {p.sale_price && <><span className="text-grey line-through">{inr(p.price)}</span><span className="bg-neon text-black text-xs font-bold px-2 py-1">SAVE {off}%</span></>}
          </div>
          <p className="text-xs text-grey mt-1">Inclusive of all taxes. Use DEN15 for 15% off your first order.</p>

          {p.colors.length > 0 && <div className="mt-8"><span className="label">Colour — {color}</span><div className="flex gap-2">{p.colors.map((c) => <button key={c} onClick={() => setColor(c)} data-testid={`color-option-${c.toLowerCase().replace(/\s/g, "-")}`} className={`chip ${color === c ? "on" : ""}`}>{c}</button>)}</div></div>}
          <div className="mt-6">
            <div className="flex justify-between items-center"><span className="label">Size {size && `— ${size}`}</span><SizeGuide bottom={bottom} /></div>
            <div className="flex flex-wrap gap-2">{p.sizes.map((s) => <button key={s} onClick={() => setSize(s)} data-testid={`size-option-${s}`} className={`chip min-w-[52px] ${size === s ? "on" : ""}`}>{s}</button>)}</div>
          </div>
          <div className="mt-6 flex items-center gap-4">
            <div className="flex items-center border border-[#333]">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3 hover:text-neon" data-testid="qty-decrease"><Minus className="w-4 h-4" /></button>
              <span className="w-10 text-center font-mono" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => Math.min(p.stock, q + 1))} className="p-3 hover:text-neon" data-testid="qty-increase"><Plus className="w-4 h-4" /></button>
            </div>
            <span className={`text-xs font-mono ${p.stock < 10 ? "text-red-400" : "text-grey"}`} data-testid="stock-info">{p.stock === 0 ? "OUT OF STOCK" : p.stock < 10 ? `HURRY — ONLY ${p.stock} LEFT` : `IN STOCK (${p.stock})`}</span>
          </div>
          <div className="grid grid-cols-[1fr_1fr_auto] gap-2 mt-6">
            <button disabled={!p.stock} onClick={() => go(false)} className="btn-ghost" data-testid="add-to-cart-button">Add to cart</button>
            <button disabled={!p.stock} onClick={() => go(true)} className="btn-neon" data-testid="buy-now-button">Buy now</button>
            <button onClick={() => toggleWish(p)} data-testid="product-wishlist-button" className={`w-14 border grid place-items-center ${inWish(p.id) ? "bg-neon text-black border-neon" : "border-[#333] hover:border-white"}`}><Heart className="w-5 h-5" fill={inWish(p.id) ? "currentColor" : "none"} /></button>
          </div>
          <p className="text-white/70 mt-8 leading-relaxed" data-testid="product-description">{p.description}</p>
          <div className="grid grid-cols-3 gap-2 mt-8 text-[11px] font-mono text-grey">
            {[[Truck, "Free ship ₹999+"], [RefreshCcw, "7-day exchange"], [ShieldCheck, "Secure payment"]].map(([I, t]) => <div key={t} className="border border-white/10 p-3 flex flex-col gap-2"><I className="w-4 h-4 text-neon" />{t}</div>)}
          </div>
        </div>
      </div>
      <Reviews product={p} reviews={data.reviews} />
      {data.related.length > 0 && <section className="mt-24"><h2 className="font-display text-4xl sm:text-5xl mb-8">YOU MIGHT ALSO VIBE WITH</h2><ProductGrid items={data.related} /></section>}
    </div>
  );
}
